import { Button, Modal, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef, useState } from "react";
import "./styles.css";

export default function PMReportDetails() {
    const location = useLocation();
    const data = location.state?.data;
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedImg, setSelectedImg] = useState<string | null>(null);

    const PLACEHOLDER_SVG = `data:image/svg+xml;utf8,
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 240'>
            <rect width='100%' height='100%' fill='%23f3f4f6'/>
            <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-size='18' fill='%239ca3af'>Image unavailable</text>
        </svg>`;

    const detailRef = useRef<HTMLDivElement>(null);
    const handleClose = () => setShow(false);

    async function toBase64(url: string): Promise<string> {
        const res = await fetch(url);
        const blob = await res.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    }

    async function convertImagesToBase64(container: HTMLElement) {
        const images = container.querySelectorAll("img");
        const promises = Array.from(images).map(async (img: HTMLImageElement) => {
            const original = img.src;
            try {
                img.src = await toBase64(original);
            } catch (e) {
                console.error("Failed to convert image:", original);
            }
        });
        await Promise.all(promises);
    }

    const exportPdf = async () => {
        const input = document.getElementById("exportContent") as HTMLElement;
        await convertImagesToBase64(input);
        html2canvas(input, {
            useCORS: true,
            allowTaint: false, // should be false for crossOrigin images
            imageTimeout: 15000,
            scrollX: 0,
            scrollY: -window.scrollY
        }).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save("report.pdf");
        });
    }

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return "—";
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateStr).toLocaleDateString(undefined, options);
    }
    const statusBadgeClass = (status: string | undefined) => {
        switch (status) {
            case 'Completed':
                return 'badge bg-success';
            case 'Pending':
                return 'badge bg-warning text-dark';
            case 'Overdue':
                return 'badge bg-danger';
            default:
                return 'badge bg-secondary';
        }
    }

    const handleOpen = (img: string | null) => {
        setSelectedImg(img);
        setShow(true);
    };

    return (
        <div ref={detailRef} className="report-container py-4 px-3">
            <div className="bg-white rounded-3 shadow-lg p-4 border">

                {/* Header: Back + Download */}
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Button variant="outline-secondary" onClick={() => window.history.back()}>
                        ← Back
                    </Button>
                    <div className="ms-auto">
                        <Button variant="primary" onClick={exportPdf} disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Download PDF"}
                        </Button>
                    </div>
                </div>

                <div id="exportContent" className="px-3 py-2">

                    {/* Title Block */}
                    <div className="text-center mb-4">
                        <h2 className="fw-bold mb-1">{data?.site_name}</h2>
                        <span className="text-muted">{formatDate(data?.maintenance_date)}</span>
                    </div>

                    {/* Overview Cards */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <div className="border rounded p-3 text-center">
                                <small className="text-muted d-block">Maintenance Date</small>
                                <h6 className="fw-semibold mt-1">{formatDate(data?.maintenance_date)}</h6>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="border rounded p-3 text-center">
                                <small className="text-muted d-block">Employee</small>
                                <h6 className="fw-semibold mt-1">{data?.employee_name ?? "—"}</h6>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="border rounded p-3 text-center">
                                <small className="text-muted d-block">Status</small>
                                <span
                                    className={`badge px-3 py-2 mt-1 ${data?.status === "COMPLETE"
                                        ? "bg-success"
                                        : "bg-danger"
                                        }`}
                                >
                                    {data?.status ?? "—"}
                                </span>
                            </div>
                        </div>

                    </div>

                    {/* Gate Reader Status */}
                    <h4 className="section-title mt-4 mb-3">Gate Reader Status</h4>
                    <div className="row g-4">
                        {data.statuses.map((item: any) => (
                            <div key={item.id} className="col-md-6">
                                <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
                                    <div className="card-body p-3">

                                        <h5 className="fw-semibold mb-2">{item.location_label}</h5>
                                        <p className="m-0"><strong>MAC:</strong> {item.reader_macid}</p>
                                        <p className="m-0"><strong>Antenna:</strong> {item.rfid_antenna}</p>
                                        <p className="m-0"><strong>PM Status:</strong> {item.pm_status}</p>

                                        <div className="d-flex justify-content-start gap-2 mt-3 flex-wrap">
                                            <img onClick={() => handleOpen(item.before_pm)} src={item.before_pm} alt="Before PM" className="report-img" />
                                            <img onClick={() => handleOpen(item.after_pm)} src={item.after_pm} alt="After PM" className="report-img" />
                                            <img onClick={() => handleOpen(item.device_alignment)} src={item.device_alignment} alt="Alignment" className="report-img" />
                                        </div>

                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Activities */}
                    <h4 className="section-title mt-5 mb-3">Activities</h4>
                    <div className="row g-3">
                        {data.activities.map((act: any) => (
                            <div key={act.id} className="col-md-6">
                                <div className="border rounded p-3 bg-light">
                                    <p className="text-dark m-0"><strong>Equipment:</strong> {act.equipment.name}</p>
                                    <p className="text-dark m-0"><strong>Activity:</strong> {act.activity.activity_name}</p>
                                    <p className="text-dark m-0"><strong>Status:</strong> {act.status}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Gate Photo */}
                    <h4 className="section-title mt-5 mb-3">Gate Photo</h4>
                    <div className="text-center">
                        <img onClick={() => handleOpen(data.gate_photo)} src={data.gate_photo} alt="Gate" className="img-fluid rounded shadow-sm border" style={{ maxWidth: "70%" }} />
                    </div>

                </div>
            </div>
            {/* Popup Dialog */}
            <Modal show={show} onHide={handleClose} centered dialogClassName="custom-modal"
                backdropClassName="custom-backdrop" >
                <Modal.Body className="text-center">
                    <img
                        src={selectedImg ?? PLACEHOLDER_SVG}
                        alt="Full Preview"
                        style={{ width: "100%", height: "auto", borderRadius: "10px" }}
                        crossOrigin="anonymous"
                        onError={(e) => (e.currentTarget.src = PLACEHOLDER_SVG)}
                    />
                </Modal.Body>
            </Modal>

        </div>

    );
}
